// Logique pure de la modale d'issue : dates des puces, validation et charge utile de l'API.

import { addDaysIso, parisDateOf } from "@shared/followup";
import type { OutcomeChoice } from "@shared/outcome";

export const RELANCE_TIMES = [
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
  "19:00",
] as const;

export type AbsentDay = "today" | "tomorrow";

export const ABSENT_DAYS: Array<{ key: AbsentDay; label: string }> = [
  { key: "today", label: "Aujourd'hui" },
  { key: "tomorrow", label: "Demain" },
];

export const BLOCKERS = [
  "Prix",
  "Réfléchir",
  "En parler à quelqu'un",
  "Pas le bon moment",
  "Autre",
] as const;

// « Autre » reste le dernier : la modale l'affiche en retrait, avec son champ libre.
export const LOSS_REASONS = [
  "Pas d'argent",
  "Pas convaincu",
  "Timing",
  "Doit en parler",
  "Ghosting",
  "Autre",
] as const;

/** Jour d'aujourd'hui à Paris (`YYYY-MM-DD`). */
export function parisToday(now: Date = new Date()): string {
  return parisDateOf(now.toISOString()) ?? now.toISOString().slice(0, 10);
}

export interface RelanceDay {
  date: string;
  /** « lun », « mar »… */
  weekday: string;
  dayOfMonth: number;
  /** « nov », « déc »… */
  month: string;
  weekend: boolean;
}

/** Les 7 jours proposés, à partir d'aujourd'hui. Calcul en UTC : le jour ne dépend pas du fuseau. */
export function relanceDays(today: string): RelanceDay[] {
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDaysIso(today, i) ?? today;
    const d = new Date(`${date}T12:00:00Z`);
    const part = (o: Intl.DateTimeFormatOptions) =>
      d.toLocaleDateString("fr-FR", { timeZone: "UTC", ...o }).replace(".", "");
    return {
      date,
      weekday: part({ weekday: "short" }),
      dayOfMonth: d.getUTCDate(),
      month: part({ month: "short" }),
      weekend: d.getUTCDay() === 0 || d.getUTCDay() === 6,
    };
  });
}

/** Jour de relance valide : aujourd'hui ou plus tard, sinon `null`. */
export function relanceDate(day: string, today: string): string | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && day >= today ? day : null;
}

export interface OutcomeFormState {
  choice: OutcomeChoice;
  relanceDay: string;
  /** HH:mm, vide : sans heure. */
  relanceTime: string;
  /** Jour du rappel après une absence. */
  absentDay: AbsentDay;
  blocker: string | null;
  note: string;
  lossReason: string | null;
  lossOther: string;
}

export interface OutcomePayload {
  choice: OutcomeChoice;
  eventId?: number;
  followUpAt?: string;
  followUpTime?: string;
  blocker?: string;
  note?: string;
  lossReason?: string;
  /** Prospect déjà acheté dont l'utilisateur a confirmé le changement d'issue. */
  force?: true;
}

export type PayloadResult = { ok: true; data: OutcomePayload } | { ok: false; error: string };

/**
 * Charge utile de `POST /api/prospects/:id/outcome`. Les champs vides sont omis. Pour les
 * « Closé », la vente est créée avant : seule la forme `{ choice, eventId }` part.
 */
export function buildOutcomePayload(
  form: OutcomeFormState,
  today: string,
  eventId?: number,
  force = false,
): PayloadResult {
  const base: OutcomePayload = { choice: form.choice };
  if (eventId !== undefined) base.eventId = eventId;
  if (force) base.force = true;
  const note = form.note.trim();
  switch (form.choice) {
    case "relance": {
      const followUpAt = relanceDate(form.relanceDay, today);
      if (!followUpAt) {
        return { ok: false, error: "Choisis une date de relance, aujourd'hui ou plus tard." };
      }
      return {
        ok: true,
        data: {
          ...base,
          followUpAt,
          ...(form.relanceTime ? { followUpTime: form.relanceTime } : {}),
          ...(form.blocker ? { blocker: form.blocker } : {}),
          ...(note ? { note } : {}),
        },
      };
    }
    case "lost": {
      const other = form.lossOther.trim();
      const lossReason = form.lossReason === "Autre" ? other || "Autre" : form.lossReason;
      return {
        ok: true,
        data: { ...base, ...(lossReason ? { lossReason } : {}), ...(note ? { note } : {}) },
      };
    }
    case "no_show": {
      const followUpAt = form.absentDay === "tomorrow" ? addDaysIso(today, 1) : today;
      return { ok: true, data: { ...base, ...(followUpAt ? { followUpAt } : {}) } };
    }
    default:
      return { ok: true, data: base };
  }
}

/**
 * Vente déjà enregistrée : statut closed_won, ou une vente existe (client repassé en relance ou
 * perdu). « Closé » ne note alors que l'issue, sans rouvrir le formulaire de vente.
 */
export function isAlreadySold(
  status: string,
  lastSaleProduct: string | null | undefined,
): boolean {
  return status === "closed_won" || (lastSaleProduct !== null && lastSaleProduct !== undefined);
}

/** Issue « Closé » selon le produit réellement saisi : Accélérateur → closed_acc, Mastery et Vidéo → closed_pc. */
export function closedChoiceFor(product: string): "closed_pc" | "closed_acc" {
  return product === "accelerateur" ? "closed_acc" : "closed_pc";
}

/**
 * Date de close proposée par le formulaire de vente ouvert depuis l'issue : jour de Paris du RDV
 * s'il est passé (le délai de rétractation part de cette date), sinon jour de Paris d'aujourd'hui.
 */
export function defaultCloseDate(eventStart: string | null | undefined, now: Date = new Date()): string {
  if (eventStart) {
    const t = new Date(eventStart).getTime();
    if (!Number.isNaN(t) && t <= now.getTime()) {
      const day = parisDateOf(eventStart);
      if (day) return day;
    }
  }
  return parisToday(now);
}
